import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Package, Truck, CheckCircle, Clock, RefreshCw, 
  ChevronDown, ChevronUp, AlertCircle, Loader2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';
import * as categoryApi from '@/services/categoryApi';

const GreigeShipmentRequests = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('internal');
  const [loading, setLoading] = useState(true);
  
  // Internal transfer shipments (Greige -> other sections)
  const [pendingShipments, setPendingShipments] = useState([]);
  
  // Category manager requests
  const [categoryRequests, setCategoryRequests] = useState([]);
  
  const [selectedTransfer, setSelectedTransfer] = useState(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      // Fetch pending internal transfer shipment requests (where Greige is the source, status = requested)
      const [internalRes, categoryRes] = await Promise.all([
        apiClient.get('/internal-transfer/shipment-requests?from_section=Greige'),
        categoryApi.getSectionIncomingRequests('Greige').catch(() => ({ requests: [] }))
      ]);
      
      setPendingShipments(internalRes.data.transfers || []);
      setCategoryRequests(categoryRes.requests || []);
    } catch (error) {
      console.error('Error fetching shipment data:', error);
      toast.error('Failed to load shipment requests');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmShipment = async () => {
    if (!selectedTransfer) return;
    
    setIsConfirming(true);
    try {
      await apiClient.post('/internal-transfer/confirm-shipment', {
        transfer_id: selectedTransfer.transfer_id
      });
      toast.success(`Shipment confirmed for ${selectedTransfer.transfer_id}`);
      setShowConfirmDialog(false);
      setSelectedTransfer(null);
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to confirm shipment');
    } finally {
      setIsConfirming(false);
    }
  };

  const openConfirmDialog = (transfer) => {
    setSelectedTransfer(transfer);
    setShowConfirmDialog(true);
  };

  const internalCount = pendingShipments.length;
  const categoryCount = categoryRequests.filter(r => r.status === 'approved').length;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FFFBF5] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#2D5F4F]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={() => navigate('/sections/greige')}
              className="text-white hover:bg-[#2D5F4F]"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold">Shipment Requests</h1>
              <p className="text-sm text-gray-400">Greige Section</p>
            </div>
          </div>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={fetchAllData}
            className="text-white border-white hover:bg-white/10"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="internal" className="flex items-center gap-2">
              <Truck className="w-4 h-4" />
              Internal Transfers
              {internalCount > 0 && (
                <Badge className="ml-1 bg-orange-500 text-white">{internalCount}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="category" className="flex items-center gap-2">
              <Package className="w-4 h-4" />
              Category Requests
              {categoryCount > 0 && (
                <Badge className="ml-1 bg-blue-500 text-white">{categoryCount}</Badge>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Internal Transfer Shipments Tab */}
          <TabsContent value="internal">
            <Card className="bg-white mb-4">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-orange-500" />
                  Pending Shipment Confirmations
                </CardTitle>
                <p className="text-sm text-gray-500">
                  These transfers have been accepted by destination sections and are waiting for you to confirm shipment.
                </p>
              </CardHeader>
            </Card>

            {pendingShipments.length === 0 ? (
              <Card className="bg-white">
                <CardContent className="p-8 text-center">
                  <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-4" />
                  <p className="text-gray-500">No pending shipment confirmations</p>
                  <p className="text-sm text-gray-400 mt-2">
                    All accepted transfers have been shipped
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {pendingShipments.map((transfer) => (
                  <Card key={transfer.transfer_id} className="bg-white shadow-md">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <h3 className="font-semibold text-lg">{transfer.transfer_id}</h3>
                          <p className="text-sm text-gray-500">
                            To: <span className="font-medium text-blue-600">{transfer.to_section}</span>
                          </p>
                        </div>
                        <Badge className="bg-yellow-100 text-yellow-800 flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          Awaiting Shipment
                        </Badge>
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4 text-sm">
                        <div>
                          <p className="text-gray-500">Bundles</p>
                          <p className="font-medium">{transfer.bundle_count || transfer.bundle_codes?.length || 0}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Requested At</p>
                          <p className="font-medium">
                            {transfer.requested_at ? new Date(transfer.requested_at).toLocaleDateString() : '-'}
                          </p>
                        </div>
                        <div>
                          <p className="text-gray-500">Accepted At</p>
                          <p className="font-medium">
                            {transfer.accepted_at ? new Date(transfer.accepted_at).toLocaleDateString() : '-'}
                          </p>
                        </div>
                        <div>
                          <p className="text-gray-500">Accepted By</p>
                          <p className="font-medium">{transfer.accepted_by_name || '-'}</p>
                        </div>
                      </div>

                      <Button
                        onClick={() => openConfirmDialog(transfer)}
                        className="w-full bg-green-600 hover:bg-green-700 text-white"
                      >
                        <Truck className="w-4 h-4 mr-2" />
                        Confirm Shipment
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Category Requests Tab */}
          <TabsContent value="category">
            {categoryRequests.length === 0 ? (
              <Card className="bg-white">
                <CardContent className="p-8 text-center">
                  <Package className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500">No category shipment requests</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {categoryRequests.map((request) => (
                  <Card key={request.request_id} className="bg-white shadow-md">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <h3 className="font-semibold">{request.request_id}</h3>
                          <p className="text-sm text-gray-500">
                            Category: {request.category_name}
                          </p>
                        </div>
                        <Badge className={
                          request.status === 'approved' ? 'bg-yellow-100 text-yellow-800' :
                          request.status === 'acknowledged' ? 'bg-blue-100 text-blue-800' :
                          request.status === 'completed' ? 'bg-green-100 text-green-800' :
                          'bg-gray-100 text-gray-800'
                        }>
                          {request.status}
                        </Badge>
                      </div>
                      <div className="text-sm text-gray-600">
                        <p>Total Qty: {request.total_shipment_qty}</p>
                        <p>Created: {new Date(request.created_at).toLocaleDateString()}</p>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>

      {/* Confirm Shipment Dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Shipment</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-gray-600 mb-4">
              You are about to confirm shipment for transfer:
            </p>
            <div className="bg-gray-50 p-4 rounded-lg space-y-2">
              <p><strong>Transfer ID:</strong> {selectedTransfer?.transfer_id}</p>
              <p><strong>Destination:</strong> {selectedTransfer?.to_section}</p>
              <p><strong>Bundles:</strong> {selectedTransfer?.bundle_count || selectedTransfer?.bundle_codes?.length || 0}</p>
            </div>
            <p className="text-sm text-gray-500 mt-4">
              This will mark the goods as shipped and notify the destination section.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfirmDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleConfirmShipment} 
              disabled={isConfirming}
              className="bg-green-600 hover:bg-green-700"
            >
              {isConfirming ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Truck className="w-4 h-4 mr-2" />
              )}
              Confirm Shipment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GreigeShipmentRequests;
